// frontend/src/services/churchService.ts

import apiClient from '../utils/apiClient';

export interface Church {
  id: number;
  name: string;
  address: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChurchWithStats extends Church {
  service_count: number;
  service_definition_count: number;
  template_count: number;
  user_count: number;
}

export interface ChurchUsageStats {
  church_id: number;
  service_count: number;
  template_count: number;
  user_count: number;
  user_churches_count: number;
  total_count: number;
  can_delete_safely: boolean;
}

export interface CreateChurchParams {
  name: string;
  address?: string | null;
}

export interface UpdateChurchParams {
  name?: string;
  address?: string | null;
  is_active?: boolean;
}

export interface ServiceDefinition {
  service_def_id: number;
  church_id: number;
  service_name: string;
  day_of_week: number;
  time: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceDefinitionParams {
  church_id: number;
  service_name: string;
  day_of_week: number;
  time: string;
  is_active?: boolean;
}

export interface UpdateServiceDefinitionParams {
  service_name?: string;
  day_of_week?: number;
  time?: string;
  is_active?: boolean;
}

// ========== Church API Functions ==========

export const getAllChurches = async (): Promise<ChurchWithStats[]> => {
  const response = await apiClient.get('/churches');
  return response.data;
};

export const getChurchById = async (id: number): Promise<Church> => {
  const response = await apiClient.get(`/churches/${id}`);
  return response.data;
};

export const createChurch = async (params: CreateChurchParams): Promise<Church> => {
  const response = await apiClient.post('/churches', params);
  return response.data;
};

export const updateChurch = async (id: number, params: UpdateChurchParams): Promise<Church> => {
  const response = await apiClient.put(`/churches/${id}`, params);
  return response.data;
};

export const getChurchUsageStats = async (id: number): Promise<ChurchUsageStats> => {
  const response = await apiClient.get(`/churches/${id}/stats`);
  return response.data;
};

export const deleteChurch = async (id: number, force: boolean = false): Promise<void> => {
  const forceParam = force ? '?force=true' : '';
  await apiClient.delete(`/churches/${id}${forceParam}`);
};

// ========== Service Definition API Functions ==========

export const getServiceDefinitionsByChurch = async (churchId: number): Promise<ServiceDefinition[]> => {
  const response = await apiClient.get(`/churches/${churchId}/service-definitions`);
  return response.data;
};

export const getServiceDefinitionById = async (id: number): Promise<ServiceDefinition> => {
  const response = await apiClient.get(`/service-definitions/${id}`);
  return response.data;
};

export const createServiceDefinition = async (params: CreateServiceDefinitionParams): Promise<ServiceDefinition> => {
  const response = await apiClient.post('/service-definitions', params);
  return response.data;
};

export const updateServiceDefinition = async (
  id: number,
  params: UpdateServiceDefinitionParams
): Promise<ServiceDefinition> => {
  const response = await apiClient.put(`/service-definitions/${id}`, params);
  return response.data;
};

export const toggleServiceDefinition = async (id: number): Promise<ServiceDefinition> => {
  const response = await apiClient.patch(`/service-definitions/${id}/toggle`);
  return response.data;
};

export const deleteServiceDefinition = async (id: number, force: boolean = false): Promise<void> => {
  const forceParam = force ? '?force=true' : '';
  await apiClient.delete(`/service-definitions/${id}${forceParam}`);
};

// ========== Helper Functions ==========

export const getDayOfWeekName = (dayOfWeek: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || 'Unknown';
};

export const formatTime12Hour = (time: string): string => {
  // Already in 12-hour format from API
  return time;
};

export const parseTime12Hour = (time: string): { hour: number; minute: number; period: 'AM' | 'PM' } => {
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    throw new Error('Invalid time format');
  }
  return {
    hour: parseInt(match[1], 10),
    minute: parseInt(match[2], 10),
    period: match[3].toUpperCase() as 'AM' | 'PM'
  };
};
